import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import moment from "moment";
// Material UI components
import {
  Card,
  Grid,
  InputLabel,
  MenuItem,
  FormControl,
  IconButton,
  Tooltip,
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import TableViewIcon from "@mui/icons-material/TableView";
// Custom components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import PlacePicker from "components/MDPlacePicker/PlacePicker";
import MDDropDown from "components/MDDropDown";
import AssignRatesToPlace from "./components/AssignRatesToPlace";
import AssignRatesTable from "./components/AssignRatesTable";
import BlackoutDateRatesToPlace from "./components/BlackoutDateRatesToPlace";
import SpecialEventsRates from "./components/SpecialEventRatesToPlace";
import CalenderView from "./components/CalenderView";
import {
  getRatesByPlaceId,
  getAssignRatesByPlaceId,
  getCalenderDataByPlaceId,
  getAssignRatesTableDataByPlaceId,
} from "store/slice/rates/rateSlice";
import { useMaterialUIController } from "context";
import AssignRatesToSpace from "./components/AssignRatesToSpace";
import { getSpaces } from "store/slice/space/spaceSlice";
import CopyAssignRates from "./components/CopyAssignRatesDialogBox";

const AssignRates = () => {
  const [controller] = useMaterialUIController();
  const { sidenavColor } = controller;
  const dispatch = useDispatch();
  const location = useLocation();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [blackoutDialogOpen, setBlackoutDialogOpen] = useState(false);
  const [specialEventDialogOpen, setSpecialEventDialogOpen] = useState(false);
  const [spaceDialogOpen, setSpaceDialogOpen] = useState(false);
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [calenderView, setCalenderView] = useState(false);

  const currentDay = moment().format("dddd").toLowerCase();
  const [selectedDay, setSelectedDay] = useState(currentDay);

  const placesData = useSelector((state) => state.places?.selectedPlace || {});
  const placeList = useSelector((state) => state.places?.placesList || []);
  const ratesData = useSelector((state) => state.rates?.ratesByPlace || []);
  const spaceData = useSelector((state) => state.spaces?.spaces || []);
  const assignRatesData = useSelector(
    (state) => state.rates?.assignRates || [],
  );
  const calenderData = useSelector(
    (state) => state.rates?.calendarAssignRates || [],
  );

  let placeId =
    placesData?._id || new URLSearchParams(location.search).get("placeId");

  const currentPlace = placeList.find((p) => p._id === placeId) || placesData;

  useEffect(() => {
    dispatch(getRatesByPlaceId(placeId));
    dispatch(getAssignRatesByPlaceId(placeId));
    dispatch(getCalenderDataByPlaceId(placeId));
    dispatch(getAssignRatesTableDataByPlaceId(placeId));
    dispatch(getSpaces(placeId));
  }, [dispatch, placeId]);

  const handleDayChange = (event) => {
    setSelectedDay(event.target.value);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={2} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={1}
                mt={2}
                py={1}
                px={1}
                bgColor={sidenavColor}
                borderRadius="lg"
                coloredShadow={sidenavColor}
                className="d-flex align-items-center gap-2"
              >
                <Grid container spacing={2} alignItems="center">
                  {/* Title */}
                  <Grid item xs={12} md="auto">
                    <MDTypography variant="h6" color="white">
                      Assign Rates
                    </MDTypography>
                  </Grid>

                  {/* Buttons */}
                  <Grid
                    item
                    xs={12}
                    md
                    display="flex"
                    justifyContent={{ xs: "flex-start", md: "flex-end" }}
                    flexWrap="wrap"
                    gap={1}
                  >
                    {placeId && (
                      <>
                        <Tooltip
                          title="Assign a new rate to the desired location or condition"
                          arrow
                        >
                          <MDButton
                            id="assign-rate-btn"
                            variant="outlined"
                            size="small"
                            onClick={() => setDialogOpen(true)}
                          >
                            Assign Rates
                          </MDButton>
                        </Tooltip>

                        <Tooltip
                          title="Assign temporary rates for special events or date ranges, visible to parkers instead of regular rates"
                          arrow
                        >
                          <MDButton
                            variant="outlined"
                            size="small"
                            onClick={() => setSpecialEventDialogOpen(true)}
                          >
                            Special Events
                          </MDButton>
                        </Tooltip>

                        <Tooltip
                          title="Set blackout periods when parking is unavailable and no rates are applied."
                          arrow
                        >
                          <MDButton
                            variant="outlined"
                            size="small"
                            onClick={() => setBlackoutDialogOpen(true)}
                          >
                            Blackout Dates
                          </MDButton>
                        </Tooltip>

                        <Tooltip title="Copy rates from another location" arrow>
                          <MDButton
                            variant="outlined"
                            size="small"
                            onClick={() => setCopyDialogOpen(true)}
                          >
                            Copy Rates
                          </MDButton>
                        </Tooltip>
                      </>
                    )}

                    <Tooltip
                      title={
                        calenderView
                          ? "Switch to Table View"
                          : "Switch to Calendar View"
                      }
                      arrow
                    >
                      <IconButton
                        onClick={() => setCalenderView(!calenderView)}
                        color="white"
                        disabled={!calenderData}
                      >
                        {calenderView ? (
                          <TableViewIcon />
                        ) : (
                          <CalendarMonthIcon />
                        )}
                      </IconButton>
                    </Tooltip>
                  </Grid>
                </Grid>
              </MDBox>

              {/* Place & Day Select Section */}
              <MDBox pt={3} sx={{ mx: 3 }}>
                <Grid container spacing={2}>
                  <Grid
                    item
                    xs={12}
                    md={calenderView ? 12 : 9}
                    lg={calenderView ? 12 : 9}
                  >
                    <PlacePicker />
                  </Grid>

                  {!calenderView && (
                    <Grid item xs={12} md={3} lg={3}>
                      <FormControl fullWidth>
                        <InputLabel id="day-select-label">
                          Select Day
                        </InputLabel>
                        <MDDropDown
                          labelId="day-select-label"
                          id="day-select"
                          label="Select Day"
                          value={selectedDay}
                          onChange={handleDayChange}
                        >
                          {[
                            "sunday",
                            "monday",
                            "tuesday",
                            "wednesday",
                            "thursday",
                            "friday",
                            "saturday",
                          ].map((day) => (
                            <MenuItem key={day} value={day}>
                              {day.charAt(0).toUpperCase() + day.slice(1)}
                            </MenuItem>
                          ))}
                        </MDDropDown>
                      </FormControl>
                    </Grid>
                  )}
                </Grid>
              </MDBox>

              {/* Dialog Components */}
              <MDBox pt={3}>
                <AssignRatesToPlace
                  placeId={placeId}
                  dialogOpen={dialogOpen}
                  ratesData={ratesData}
                  onClose={() => setDialogOpen(false)}
                  occupancy={currentPlace?.spaces}
                  spacesData={spaceData?.filter((s) => s.status === 10)}
                  placeData={currentPlace}
                />

                <BlackoutDateRatesToPlace
                  placeId={placeId}
                  dialogOpen={blackoutDialogOpen}
                  updateParentData={ratesData}
                  onClose={() => setBlackoutDialogOpen(false)}
                />

                <SpecialEventsRates
                  placeId={placeId}
                  ratesData={ratesData}
                  dialogOpen={specialEventDialogOpen}
                  onClose={() => setSpecialEventDialogOpen(false)}
                  occupancy={currentPlace?.spaces}
                  spacesData={spaceData?.filter((s) => s.status === 10)}
                  placeData={currentPlace}
                />

                <AssignRatesToSpace
                  placeId={placeId}
                  dialogOpen={spaceDialogOpen}
                  ratesData={ratesData}
                  spacesData={spaceData}
                  onClose={() => setSpaceDialogOpen(false)}
                  occupancy={currentPlace?.spaces}
                />

                <CopyAssignRates
                  fromPlaceId={placeId}
                  dialogOpen={copyDialogOpen}
                  placeList={placeList}
                  onClose={() => setCopyDialogOpen(false)}
                />
              </MDBox>

              {/* Main Table / Calendar Section */}
              <MDBox>
                {calenderView ? (
                  <CalenderView calenderData={calenderData} />
                ) : (
                  <AssignRatesTable
                    placeId={placeId}
                    selectedDay={selectedDay}
                    assignRatesData={assignRatesData || []}
                    placeData={currentPlace}
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
};

export default AssignRates;
